import { UpperCasePipe } from '@angular/common'
import { Component, inject } from '@angular/core'
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router'

import { MsalService } from '@azure/msal-angular'
import { TranslatePipe } from '@ngx-translate/core'
import { CheckboxModule } from 'primeng/checkbox'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { InputComponent } from 'app/_shared/components/input/input.component'
import { SsoConfig } from 'app/_shared/interfaces/SsoConfig'
import { LoginResponse, UserLogin } from 'app/_shared/interfaces/auth'
import { AuthService } from 'app/_shared/services/auth.service'
import { OktaAuthService } from 'app/_shared/services/okta-auth.service'
import { ToastService } from 'app/_shared/services/toast.service'
import { domainEmailValidator } from 'app/_shared/validators/domain-email'
import { AppConfigService } from 'app/app-config.service'
import { UserStore } from 'app/store/user.store'

@Component({
  selector: 'app-login',
  imports: [
    CheckboxModule,
    FormsModule,
    RouterLink,
    InputComponent,
    RouterLinkActive,
    TranslatePipe,
    ReactiveFormsModule,
    UpperCasePipe,
    ButtonComponent
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  authService: AuthService = inject(AuthService)
  userStore = inject(UserStore)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private appConfigService: AppConfigService = inject(AppConfigService)
  toastService = inject(ToastService)
  loading: boolean = false
  msalService = inject(MsalService)
  private oktaAuthService = inject(OktaAuthService)

  get storefrontId(): number | null {
    return this.appConfigService.getAppConfig.id
  }

  private sso: SsoConfig | undefined = this.appConfigService.getAppConfig?.sso
  private private_store = this.appConfigService.getAppConfig?.private_store
  private domain = this.appConfigService.getAppConfig?.domain
  ssoEnabled = this.sso?.enabled ?? false
  ssoProvider = this.sso?.provider

  loginForm = new FormGroup({
    email: new FormControl('', {
      validators: [
        Validators.required,
        Validators.email,
        domainEmailValidator(this.private_store, this.domain)
      ],
      nonNullable: true
    }),
    password: new FormControl('', { validators: [Validators.required], nonNullable: true })
  })

  onSubmit() {
    this.loading = true
    if (this.loginForm.valid) {
      const payload: UserLogin = {
        ...this.loginForm.getRawValue(),
        storefront_id: this.storefrontId
      }

      this.authService.login(payload).subscribe({
        next: res => {
          this.loading = false
          const data = res.data as LoginResponse
          this.userStore.storeUser(data.user, data.access_token)
          const returnUrl =
            data.user.role?.name === 'storefront-admin'
              ? '/my-account/admin/orders'
              : this.activatedRoute.snapshot.queryParams['returnUrl'] || '/'
          this.router.navigateByUrl(returnUrl)
        },
        error: err => {
          this.loading = false
          this.toastService.error(err.error.message)
        }
      })
    } else {
      this.loading = false
    }
  }

  onMicrosoftEntraLogin() {
    const state = JSON.stringify({ subdomain: window.location.hostname })
    this.msalService.loginRedirect({
      scopes: ['user.read'],
      state,
      redirectStartPage: window.location.origin
    })
  }

  onOktaLogin() {
    this.oktaAuthService.loginRedirect().catch(error => {
      console.error('Okta login redirect error:', error)
    })
  }
}
